import { Card } from '../components/ui/Card.jsx';
import { useCurrency } from '../currency/useCurrency.js';
import { useTranslation } from '../i18n/useTranslation.js';
import { useTheme } from '../theme/useTheme.js';

function OptionRow({ value, label, description, selected, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`option-card${selected ? ' selected' : ''}`}
      onClick={() => onSelect(value)}
    >
      <span className="option-indicator" aria-hidden="true" />
      <span className="option-card-text">
        <span className="option-label">{label}</span>
        <span className="option-description">{description}</span>
      </span>
    </button>
  );
}

/** Appearance / language / currency - the personal "general" settings group. */
export function GeneralSettings() {
  const { t, locale, setLocale, supportedLocales } = useTranslation();
  const { themeChoice, activeTheme, setThemeChoice } = useTheme();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();

  const themeOptions = [
    { value: 'light',  label: t('settings.theme.light'),  description: t('settings.theme.light.desc') },
    { value: 'dark',   label: t('settings.theme.dark'),   description: t('settings.theme.dark.desc') },
    { value: 'system', label: t('settings.theme.system'), description: t('settings.theme.system.desc') },
  ];

  const languageOptions = supportedLocales.map((code) => ({
    value: code,
    label: t(`settings.language.${code}`),
    description: t(`settings.language.${code}.desc`),
  }));

  const currencyOptions = supportedCurrencies.map((code) => ({
    value: code,
    label: t(`settings.currency.${code}`),
    description: t(`settings.currency.${code}.desc`),
  }));

  const activeLabel = activeTheme === 'dark' ? t('settings.theme.dark') : t('settings.theme.light');

  return (
    <>
      <Card title={t('settings.appearance')}>
        <p className="muted text-sm" style={{ marginTop: 0 }}>
          {t('settings.appearance.intro')} {t('settings.appearance.active')}{' '}
          <strong>{activeLabel}</strong>
          {themeChoice === 'system' && ` ${t('settings.appearance.followsSystem')}`}.
        </p>
        <div className="option-group" role="radiogroup" aria-label={t('settings.appearance')}>
          {themeOptions.map((option) => (
            <OptionRow
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              selected={themeChoice === option.value}
              onSelect={setThemeChoice}
            />
          ))}
        </div>
      </Card>

      <Card title={t('settings.language')}>
        <p className="muted text-sm" style={{ marginTop: 0 }}>
          {t('settings.language.intro')}
        </p>
        <div className="option-group" role="radiogroup" aria-label={t('settings.language')}>
          {languageOptions.map((option) => (
            <OptionRow
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              selected={locale === option.value}
              onSelect={setLocale}
            />
          ))}
        </div>
      </Card>

      <Card title={t('settings.currency')}>
        <p className="muted text-sm" style={{ marginTop: 0 }}>
          {t('settings.currency.intro')}
        </p>
        <div className="option-group" role="radiogroup" aria-label={t('settings.currency')}>
          {currencyOptions.map((option) => (
            <OptionRow
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              selected={currency === option.value}
              onSelect={setCurrency}
            />
          ))}
        </div>
      </Card>
    </>
  );
}
